// --- Dependencies -------------------------------------------------------------------------------
import { Switch } from "@mui/material";
import { useEffect, useState } from "react";
// --- Our Modules --------------------------------------------------------------------------------
import useFetch from "@/hooks/useFetch";
import useTranslation from "@/hooks/useTranslation";
import type { TyApiResponse, TyClient } from "@/types";
import InputGroups from "@/UI/components/InputGroups";
import BranchAccessInput from "@/UI/inputs/BranchAccessInput";
import "./styles.css";

// --- Types --------------------------------------------------------------------------------------

type ActionType = "view" | "edit";

type PermissionState = {
    model: string;
    actions: ActionType[];
};

export type EditClientPermissionsPopupDetails = {
    client: TyClient;
    onSuccess?: () => void;
};

type Props = {
    onClose: () => void;
    details?: EditClientPermissionsPopupDetails;
};

type TyBranchOption = { _id: string; name: string };

type FormState = {
    makeOwner: boolean;
    permissions: PermissionState[];
    branchAccess: "all" | string[];
};

// --- Constants ----------------------------------------------------------------------------------

const MODELS = ["menu", "branches", "clients", "subscriptions", "settings"] as const;
const ACTIONS: ActionType[] = ["view", "edit"];

const FULL_PERMISSIONS: PermissionState[] = MODELS.map((model) => ({ model, actions: [...ACTIONS] }));

const switchSx = {
    width: 34,
    height: 21,
    padding: 0,
    "& .MuiSwitch-switchBase": {
        padding: 0,
        margin: "2.5px",
        transitionDuration: "200ms",
        "&.Mui-checked": {
            transform: "translateX(13px)",
            color: "var(--color-white)",
            "& + .MuiSwitch-track": { backgroundColor: "var(--color-brand)", opacity: 1, border: 0 },
        },
    },
    "& .MuiSwitch-thumb": { width: 16, height: 16, boxShadow: "0 1px 4px rgba(0,0,0,0.2)" },
    "& .MuiSwitch-track": { borderRadius: "var(--radius-full)", backgroundColor: "var(--color-gray-01)", opacity: 1 },
};

// --- Helpers ------------------------------------------------------------------------------------

function buildInitialForm(client: TyClient): FormState {
    const access = client.storeAccess;
    const rawBranches = access?.branches;
    const branchAccess: "all" | string[] =
        rawBranches === "all" || rawBranches == null ? "all" : (rawBranches as { _id: string; name: string }[]).map((b) => b._id);

    return {
        makeOwner: client.isOwner,
        permissions: client.isOwner
            ? [...FULL_PERMISSIONS]
            : (access?.permissions ?? []).map((p) => ({
                  model: p.model,
                  actions: p.actions as ActionType[],
              })),
        branchAccess,
    };
}

// --- Logic --------------------------------------------------------------------------------------

export default function EditClientPermissionsPopup({ onClose, details }: Props) {
    const t = useTranslation();
    const client = details?.client;

    const [form, setForm] = useState<FormState>(() =>
        client ? buildInitialForm(client) : { makeOwner: false, permissions: [], branchAccess: "all" }
    );

    const setField = <K extends keyof FormState>(key: K, value: FormState[K]) => setForm((prev) => ({ ...prev, [key]: value }));

    const handleMakeOwnerChange = (checked: boolean) => {
        setForm((prev) => ({ ...prev, makeOwner: checked, permissions: checked ? FULL_PERMISSIONS : [] }));
    };

    // --- Branch data ----------------------------------------------------------------------------

    const { data: branchesData } = useFetch<TyApiResponse<{ branches: TyBranchOption[] }>>("/branches", {}, true);
    const branchOptions: TyBranchOption[] = branchesData?.data?.branches ?? [];

    // --- Permission logic -----------------------------------------------------------------------

    const hasAction = (model: string, action: ActionType) => form.permissions.find((p) => p.model === model)?.actions.includes(action) ?? false;

    const ensureView = (perms: PermissionState[]): PermissionState[] =>
        perms.map((p) => {
            const hasEdit = p.actions.includes("edit");
            if (hasEdit && !p.actions.includes("view")) return { ...p, actions: ["view", ...p.actions] as ActionType[] };
            return p;
        });

    const toggleAction = (model: string, action: ActionType) => {
        const prev = form.permissions;
        const existing = prev.find((p) => p.model === model);
        let updated: PermissionState[];

        if (existing) {
            const has = existing.actions.includes(action);
            if (has) {
                const next = existing.actions.filter((a) => a !== action);
                updated =
                    next.length === 0 ? prev.filter((p) => p.model !== model) : prev.map((p) => (p.model === model ? { ...p, actions: next } : p));
            } else {
                updated = prev.map((p) => (p.model === model ? { ...p, actions: [...p.actions, action] } : p));
            }
        } else {
            updated = [...prev, { model, actions: [action] }];
        }

        setField("permissions", ensureView(updated));
    };

    const getModelCheckState = (model: string): "none" | "some" | "all" => {
        const perm = form.permissions.find((p) => p.model === model);
        if (!perm || perm.actions.length === 0) return "none";
        if (ACTIONS.every((a) => perm.actions.includes(a))) return "all";
        return "some";
    };

    const toggleModelAll = (model: string) => {
        const state = getModelCheckState(model);
        const prev = form.permissions;
        let updated: PermissionState[];

        if (state === "all") {
            updated = prev.filter((p) => p.model !== model);
        } else {
            const existing = prev.find((p) => p.model === model);
            updated = existing
                ? prev.map((p) => (p.model === model ? { ...p, actions: [...ACTIONS] } : p))
                : [...prev, { model, actions: [...ACTIONS] }];
        }

        setField("permissions", updated);
    };

    const getActionCheckState = (action: ActionType): "none" | "some" | "all" => {
        const count = MODELS.filter((m) => hasAction(m, action)).length;
        if (count === 0) return "none";
        if (count === MODELS.length) return "all";
        return "some";
    };

    const toggleActionAll = (action: ActionType) => {
        const state = getActionCheckState(action);
        let updated = [...form.permissions];

        if (state === "all") {
            updated = updated.map((p) => ({ ...p, actions: p.actions.filter((a) => a !== action) })).filter((p) => p.actions.length > 0);
        } else {
            for (const model of MODELS) {
                const idx = updated.findIndex((p) => p.model === model);
                if (idx >= 0) {
                    if (!updated[idx].actions.includes(action)) updated[idx] = { ...updated[idx], actions: [...updated[idx].actions, action] };
                } else {
                    updated.push({ model, actions: [action] });
                }
            }
        }

        setField("permissions", ensureView(updated));
    };

    // --- Submit ---------------------------------------------------------------------------------

    const {
        data: submitData,
        loading: submitting,
        error,
        invoke,
    } = useFetch<TyApiResponse<Record<string, never>>>(`/clients/${client?._id ?? "x"}`, { method: "PUT" }, false);

    useEffect(() => {
        if (!submitData?.success) return;
        details?.onSuccess?.();
        onClose();
    }, [submitData]);

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        invoke({
            data: {
                makeOwner: form.makeOwner,
                permissions: form.permissions.map((p) => ({ model: p.model, actions: p.actions })),
                branches: form.branchAccess,
            },
        });
    };

    // --- Labels ---------------------------------------------------------------------------------

    const modelLabels: Record<string, string> = {
        menu: t.sideBar.menu,
        branches: t.sideBar.branches,
        clients: t.sideBar.clients,
        subscriptions: t.sideBar.subscriptions,
        settings: t.sideBar.settings,
    };

    const actionLabels: Record<ActionType, string> = {
        view: t.formClient.view,
        edit: t.formClient.edit,
    };

    // --- Render ---------------------------------------------------------------------------------

    return (
        <div className="edit-client-perms-popup">
            {/* --- Header --------------------------------------------------------------------- */}
            <div className="ecpp-header">
                <p className="ecpp-title">{t.formClient.editPermissionsTitle}</p>
                <button type="button" className="ecpp-close" onClick={onClose} aria-label="Close">
                    <img src="/icons/close-icon.svg" alt="" width={16} height={16} />
                </button>
            </div>

            {/* --- Body ---------------------------------------------------------------------- */}
            <form className="ecpp-body" onSubmit={handleSubmit} noValidate>
                <div className="ecpp-fields">
                    {/* Access */}
                    <InputGroups title={t.formClient.accessSection}>
                        <div className="ecpp-toggle-row">
                            <div className="ecpp-toggle-info">
                                <span className="ecpp-toggle-label">{t.formClient.makeOwnerLabel}</span>
                                <span className="ecpp-toggle-hint">{t.formClient.makeOwnerHint}</span>
                            </div>
                            <Switch checked={form.makeOwner} onChange={(e) => handleMakeOwnerChange(e.target.checked)} sx={switchSx} />
                        </div>
                    </InputGroups>

                    {/* Branches — chosen before the permission matrix, matching the create form */}
                    {!form.makeOwner && branchOptions.length > 0 && (
                        <InputGroups title={t.formClient.branchesSection}>
                            <BranchAccessInput
                                value={form.branchAccess}
                                onChange={(value) => setField("branchAccess", value)}
                                options={branchOptions}
                                disabled={submitting}
                            />
                        </InputGroups>
                    )}

                    {/* Permissions */}
                    <InputGroups title={t.formClient.permissionsSection}>
                        <div className="ecpp-table-wrap">
                            <table className="ecpp-table">
                                <thead>
                                    <tr>
                                        <th className="ecpp-th ecpp-th--model">{t.formClient.modelColumn}</th>
                                        {ACTIONS.map((action) => {
                                            const state = getActionCheckState(action);
                                            return (
                                                <th key={action} className="ecpp-th">
                                                    <div className="ecpp-th-inner">
                                                        <span>{actionLabels[action]}</span>
                                                        <button
                                                            type="button"
                                                            className={`ecpp-checkbox ecpp-checkbox--sm${state === "all" ? " checked" : state === "some" ? " partial" : ""}`}
                                                            onClick={() => toggleActionAll(action)}
                                                            disabled={form.makeOwner}
                                                            aria-label={`Toggle all ${action}`}
                                                        />
                                                    </div>
                                                </th>
                                            );
                                        })}
                                    </tr>
                                </thead>
                                <tbody>
                                    {MODELS.map((model) => {
                                        const modelState = getModelCheckState(model);
                                        return (
                                            <tr key={model} className="ecpp-tr">
                                                <td className="ecpp-td ecpp-td--model">
                                                    <div className="ecpp-model-cell">
                                                        <button
                                                            type="button"
                                                            className={`ecpp-checkbox${modelState === "all" ? " checked" : modelState === "some" ? " partial" : ""}`}
                                                            onClick={() => toggleModelAll(model)}
                                                            disabled={form.makeOwner}
                                                            aria-label={`Toggle all for ${model}`}
                                                        />
                                                        <span className="ecpp-model-name">{modelLabels[model] ?? model}</span>
                                                    </div>
                                                </td>
                                                {ACTIONS.map((action) => (
                                                    <td key={action} className="ecpp-td">
                                                        <button
                                                            type="button"
                                                            className={`ecpp-checkbox${hasAction(model, action) ? " checked" : ""}`}
                                                            onClick={() => toggleAction(model, action)}
                                                            disabled={form.makeOwner}
                                                            aria-label={`${action} ${model}`}
                                                        />
                                                    </td>
                                                ))}
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </InputGroups>

                    {error && <p className="ecpp-error">{error}</p>}
                </div>

                {/* --- Footer ----------------------------------------------------------------- */}
                <div className="ecpp-footer">
                    <button type="button" className="ecpp-btn ecpp-btn--cancel" onClick={onClose} disabled={submitting}>
                        {t.formClient.cancel}
                    </button>
                    <button type="submit" className="ecpp-btn ecpp-btn--submit" disabled={submitting}>
                        {submitting ? <span className="ecpp-spinner" /> : t.formClient.submitEdit}
                    </button>
                </div>
            </form>
        </div>
    );
}
